import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, RefreshCw } from "lucide-react";
import InlineSpinner from "../../components/shared/Spinner/InlineSpinner";
import AlertBanner from "../../components/shared/AlertBanner/AlertBanner";
import { updateProviderFromDraft, readFileContent, listProviders, getProviderCredentials, saveProviderCredentials } from "../../api/providers";
import Loader from "../../components/Loader/Loader";
import UserAuthStore from "../../store/userStore";
import UIStore from "../../store/uiStore";
import Header from "../../components/Header/Header";
import ModelsHeader from "../../components/Settings/ModelsHeader";
import { CompactButton, CompactPrimaryButton, PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import FormInfo from "../../components/Inputs/FormInfo";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import FileUploadInput from "../../components/Inputs/FileUploadInput";
import FileEditorSection from "../../components/Forge/FileEditorSection";
import EntityAuditStrip from "../../components/Forge/EntityAuditStrip";
import Toggle from "../../components/shared/Toggle";
import ROUTES from "../../router/routes";
import { apiUrl } from "../../config";
import { useFormik } from "formik";
import * as Yup from "yup";
import logger from "../../services/Logger";
import useProviderDraft from "../../hooks/useProviderDraft";

const FIELD_LABEL_CLASS = "flex items-center gap-1 text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 mb-1.5";
const HELP_TEXT_CLASS = "text-[11px] text-ink-gray-400 mt-1";
const SECTION_HEADING_CLASS = "text-sm font-semibold text-ink-gray-900 dark:text-ink-white mb-4";

const EditProviderPage = ({ settingsMode = false }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { providerId } = useParams();
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const orgAvailable = UIStore((state) => state.orgAvailable);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [providerAudit, setProviderAudit] = useState(null);
  const returnRoute = settingsMode ? ROUTES.SETTINGS.FORGE_PROVIDERS : ROUTES.FORGE.ROOT;

  useEffect(() => {
    if (settingsMode && orgAvailable === false) {
      navigate(ROUTES.SETTINGS.ROOT, { replace: true });
    }
  }, [navigate, orgAvailable, settingsMode]);

  // Credential display state
  const [credentialInfo, setCredentialInfo] = useState(null);
  const [isLoadingCreds, setIsLoadingCreds] = useState(false);
  const [credentialLoadError, setCredentialLoadError] = useState(null);
  const [credentialReloadKey, setCredentialReloadKey] = useState(0);
  const [isRotating, setIsRotating] = useState(false);
  const [rotateClientId, setRotateClientId] = useState("");
  const [rotateClientSecret, setRotateClientSecret] = useState("");
  const [isSavingCreds, setIsSavingCreds] = useState(false);
  const [credError, setCredError] = useState(null);
  const [credSuccess, setCredSuccess] = useState(false);

  const [scopesLoadError, setScopesLoadError] = useState(null);
  const { resetDraft, ...providerDraft } = useProviderDraft();

  const [initialFormData, setInitialFormData] = useState({
    provider_id: "",
    name: "",
    service_type: "",
    auth_format: "raw_oauth2",
    auth_endpoint: "",
    token_endpoint: "",
    redirect_uri: `${apiUrl}/oauth/callback`,
    token_auth_method: "",
    userinfo_endpoint: "",
    scopes_path: "",
    icon_path: "",
    enabled: true,
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Display Name is required"),
    service_type: Yup.string()
      .trim()
      .required("Service Type is required"),
    auth_format: Yup.string()
      .trim()
      .required("Credential Format is required"),
    auth_endpoint: Yup.string()
      .url("Must be a valid URL")
      .required("Authorization Endpoint is required"),
    token_endpoint: Yup.string()
      .url("Must be a valid URL")
      .required("Token Endpoint is required"),
    redirect_uri: Yup.string()
      .url("Must be a valid URL")
      .required("Redirect URI is required"),
    userinfo_endpoint: Yup.string()
      .url("Must be a valid URL")
      .required("Userinfo Endpoint is required"),
    token_auth_method: Yup.string()
      .required("Token Authentication Method is required"),
    scopes_path: Yup.string()
      .required("Scope configuration is required"),
  });

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: handleFormSubmit,
  });

  const {
    values,
    errors,
    touched,
    dirty,
    handleChange,
    handleBlur,
    setFieldValue,
    submitCount,
  } = formik;

  // Load existing provider data
  useEffect(() => {
    const fetchProvider = async () => {
      try {
        setIsLoading(true);
        const providers = await listProviders(orgId);
        const provider = providers.find((p) => p.provider_id === providerId);

        if (provider) {
          if (provider.system_managed) {
            navigate(returnRoute, { replace: true });
            return;
          }
          setInitialFormData({
            provider_id: provider.provider_id || "",
            name: provider.name || "",
            service_type: provider.service_type || "",
            auth_format: provider.auth_format || "raw_oauth2",
            auth_endpoint: provider.oauth_config?.auth_endpoint || "",
            token_endpoint: provider.oauth_config?.token_endpoint || "",
            redirect_uri: provider.oauth_config?.redirect_uri || `${apiUrl}/oauth/callback`,
            token_auth_method: provider.oauth_config?.token_auth_method || "",
            userinfo_endpoint: provider.oauth_config?.userinfo_endpoint || "",
            scopes_path: provider.oauth_config?.scopes_path || "",
            icon_path: provider.icon_path || "",
            enabled: provider.enabled !== false,
          });
          setProviderAudit({
            created_by: provider.created_by || null,
            created_by_name: provider.created_by_name || null,
            created_by_email: provider.created_by_email || null,
            created_at: provider.created_at || null,
            updated_by: provider.updated_by || null,
            updated_by_name: provider.updated_by_name || null,
            updated_by_email: provider.updated_by_email || null,
            updated_at: provider.updated_at || null,
          });
          try {
            const scopes = await readFileContent(provider.oauth_config?.scopes_path, orgId);
            const scopesContent = JSON.stringify(JSON.parse(scopes.content), null, 2);
            resetDraft({
              scopesContent,
              iconPath: provider.icon_path || "",
            });
            setScopesLoadError(null);
          } catch (scopesError) {
            setScopesLoadError(
              scopesError.response?.data?.detail || "Failed to load scopes file"
            );
            logger.error('EditProviderPage', 'Failed to load provider scopes', { error: scopesError.message, providerId });
          }
        } else {
          setError("Provider not found");
        }
      } catch (err) {
        setError("Failed to load provider");
        logger.error('EditProviderPage', 'Failed to load provider', { error: err.message, providerId });
      } finally {
        setIsLoading(false);
      }
    };

    if (orgId && providerId) {
      fetchProvider();
    }
  }, [navigate, orgId, providerId, resetDraft, returnRoute]);

  useEffect(() => {
    if (!orgId || !providerId || providerId === "photon" || !user.uid) return undefined;

    let cancelled = false;
    const fetchCredentials = async () => {
      setIsLoadingCreds(true);
      setCredentialLoadError(null);
      try {
        const creds = await getProviderCredentials(orgId, user.uid, providerId);
        if (!cancelled) setCredentialInfo(creds);
      } catch (err) {
        if (!cancelled) {
          setCredentialInfo(null);
          setCredentialLoadError("Unable to load provider credentials.");
        }
        logger.error('EditProviderPage', 'Failed to load credentials', { error: err.message, providerId });
      } finally {
        if (!cancelled) setIsLoadingCreds(false);
      }
    };

    fetchCredentials();
    return () => {
      cancelled = true;
    };
  }, [orgId, providerId, user.uid, credentialReloadKey]);

  async function handleSaveCredentials() {
    if (!rotateClientId.trim() || !rotateClientSecret.trim()) {
      setCredError("Both Client ID and Client Secret are required");
      return;
    }
    setIsSavingCreds(true);
    setCredError(null);
    setCredSuccess(false);
    try {
      await saveProviderCredentials(orgId, user.uid, providerId, rotateClientId.trim(), rotateClientSecret.trim());
      const creds = await getProviderCredentials(orgId, user.uid, providerId);
      setCredentialInfo(creds);
      setIsRotating(false);
      setRotateClientId("");
      setRotateClientSecret("");
      setCredSuccess(true);
      setTimeout(() => setCredSuccess(false), 4000);
    } catch (err) {
      setCredError(
        err.response?.data?.error
        || err.response?.data?.detail
        || "Failed to save credentials"
      );
    } finally {
      setIsSavingCreds(false);
    }
  }

  async function handleFormSubmit(formValues) {
    if (providerDraft.hasPendingEditorTransaction) {
      setError("Apply or discard the open scopes changes before saving the provider.");
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      const providerData = {
        name: formValues.name,
        service_type: formValues.service_type,
        auth_format: formValues.auth_format,
        oauth_config: {
          auth_endpoint: formValues.auth_endpoint,
          token_endpoint: formValues.token_endpoint,
          token_auth_method: formValues.token_auth_method,
          redirect_uri: formValues.redirect_uri,
          userinfo_endpoint: formValues.userinfo_endpoint,
        },
        enabled: formValues.enabled,
      };

      await updateProviderFromDraft(providerId, providerData, {
        scopes: providerDraft.scopesDraft,
        expectedUpdatedAt: providerAudit?.updated_at || null,
        iconAction: providerDraft.iconAction,
        icon: providerDraft.iconFile,
      });
      await queryClient.invalidateQueries({ queryKey: ["get-providers", orgId] });
      navigate(returnRoute);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || "Failed to update provider");
      setIsSubmitting(false);
    }
  }

  // formik.dirty is reliable here (unlike EditAgentPage.js): initialFormData is
  // a state variable updated via setInitialFormData once the real provider
  // loads, and enableReinitialize:true makes Formik re-sync its pristine
  // snapshot against it — so dirty correctly starts false right after load and
  // flips true only once the user actually changes a field.
  const isDisabled = isSubmitting || Boolean(scopesLoadError) || (
    !dirty && !providerDraft.isDraftDirty && !providerDraft.hasPendingEditorTransaction
  );

  if (isLoading) {
    return (
      <div className="w-full">
        {settingsMode
          ? <ModelsHeader title="Forge Providers" onBack={() => navigate(returnRoute)} />
          : <Header type="forge" />}
        <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <Loader variant="form" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {settingsMode
        ? <ModelsHeader title="Forge Providers" onBack={() => navigate(returnRoute)} />
        : <Header type="forge" />}
      <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
        <div className="max-w-7xl mx-auto px-8 py-6">
          {/* Main Content Container */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-lg shadow-sm border border-edge-gray-200 dark:border-edge-gray-700 p-8">
            {/* Header section */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-lg font-semibold text-ink-ink dark:text-ink-white">
                  Edit Provider
                </h1>
                <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mt-1">
                  Update OAuth provider configuration
                </p>
              </div>
              <div className="flex items-center gap-3">
                <SecondaryButton
                  type="button"
                  onClick={() => navigate(returnRoute)}
                  className="px-4 py-2"
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  form="edit-provider-form"
                  onClick={(event) => {
                    if (providerDraft.hasPendingEditorTransaction) {
                      event.preventDefault();
                      setError("Apply or discard the open scopes changes before saving the provider.");
                    }
                  }}
                  disabled={isDisabled || isSubmitting}
                  className="px-6 py-2"
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </PrimaryButton>
              </div>
            </div>

            <hr className="border-edge-gray-200 dark:border-edge-gray-700 mb-8" />

            {error && <AlertBanner className="mb-6">{error}</AlertBanner>}

            <form id="edit-provider-form" onSubmit={formik.handleSubmit}>
              <div className="space-y-8">
                {/* Section 1: Basic Information */}
                <div>
                  <h3 className={SECTION_HEADING_CLASS}>Basic Information</h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Provider ID
                        </label>
                        <Input
                          type="text"
                          name="provider_id"
                          value={values.provider_id}
                          disabled
                        />
                        <p className={HELP_TEXT_CLASS}>Cannot be changed</p>
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Display Name <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g., Google Workspace"
                          error={Boolean(errors.name && (touched.name || submitCount > 0))}
                        />
                        {errors.name && (touched.name || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.name}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Service Type <span className="text-ink-red-500">*</span>
                          <FormInfo content="Internal identifier used to namespace and retrieve this provider's tokens." />
                        </label>
                        <Input
                          type="text"
                          name="service_type"
                          value={values.service_type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g., google"
                          error={Boolean(errors.service_type && (touched.service_type || submitCount > 0))}
                        />
                        {errors.service_type && (touched.service_type || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.service_type}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Auth Format and Icon */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Credential Format <span className="text-ink-red-500">*</span>
                          <FormInfo content="Determines how provider credentials are handled, such as raw OAuth2, Google SDK, or Microsoft MSAL." />
                        </label>
                        <Input
                          type="text"
                          name="auth_format"
                          value={values.auth_format}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g., raw_oauth2, google_sdk"
                          error={Boolean(errors.auth_format && (touched.auth_format || submitCount > 0))}
                        />
                        <p className={HELP_TEXT_CLASS}>
                          e.g., raw_oauth2, google_sdk, microsoft_msal
                        </p>
                        {errors.auth_format && (touched.auth_format || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.auth_format}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Icon
                        </label>
                        <FileUploadInput
                          orgId={orgId}
                          uploadType="provider_icon"
                          entityId={values.provider_id}
                          accept=".svg,.png,.jpg,.jpeg,.webp"
                          initialFilePath={values.icon_path}
                          placeholder="Drop icon or click to browse"
                          deferUpload
                          onFileSelected={providerDraft.selectIcon}
                        />
                        <p className={HELP_TEXT_CLASS}>SVG, PNG, JPG or WebP</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: OAuth Configuration */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>OAuth Configuration</h3>
                  <div className="space-y-6">
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Redirect URI <span className="text-ink-red-500">*</span>
                        <FormInfo content="Where the provider returns users after authorization. It must exactly match the URI registered in the OAuth app settings." />
                      </label>
                      <Input
                        type="url"
                        name="redirect_uri"
                        value={values.redirect_uri}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="https://your-api-domain.com/oauth/callback"
                        error={Boolean(errors.redirect_uri && (touched.redirect_uri || submitCount > 0))}
                      />
                      <p className={HELP_TEXT_CLASS}>Must exactly match the authorized redirect URI registered for this OAuth client.</p>
                      {errors.redirect_uri && (touched.redirect_uri || submitCount > 0) && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.redirect_uri}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Authorization Endpoint <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="url"
                          name="auth_endpoint"
                          value={values.auth_endpoint}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="https://accounts.google.com/o/oauth2/v2/auth"
                          error={Boolean(errors.auth_endpoint && (touched.auth_endpoint || submitCount > 0))}
                        />
                        {errors.auth_endpoint && (touched.auth_endpoint || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.auth_endpoint}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Token Endpoint <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="url"
                          name="token_endpoint"
                          value={values.token_endpoint}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="https://oauth2.googleapis.com/token"
                          error={Boolean(errors.token_endpoint && (touched.token_endpoint || submitCount > 0))}
                        />
                        {errors.token_endpoint && (touched.token_endpoint || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.token_endpoint}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Userinfo Endpoint <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="url"
                          name="userinfo_endpoint"
                          value={values.userinfo_endpoint}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="https://www.googleapis.com/oauth2/v3/userinfo"
                          error={Boolean(errors.userinfo_endpoint && (touched.userinfo_endpoint || submitCount > 0))}
                        />
                        {errors.userinfo_endpoint && (touched.userinfo_endpoint || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.userinfo_endpoint}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Token Authentication Method <span className="text-ink-red-500">*</span>
                        <FormInfo content="Controls whether client credentials are sent in the token request body or the Authorization header." />
                      </label>
                      <Select
                        value={values.token_auth_method}
                        onChange={(val) => setFieldValue("token_auth_method", val)}
                        placeholder="Select authentication method"
                        error={!!(errors.token_auth_method && (touched.token_auth_method || submitCount > 0))}
                        options={[
                          { value: "form",  label: "Form-based", description: "Client credentials in body (Google, HubSpot)" },
                          { value: "basic", label: "Basic Auth",  description: "Credentials in header (ZoomInfo, Okta, Auth0)" },
                        ]}
                      />
                      {errors.token_auth_method && (touched.token_auth_method || submitCount > 0) && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.token_auth_method}</p>
                      )}
                      <p className={HELP_TEXT_CLASS}>
                        How client credentials are sent to the token endpoint
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 3: App Credentials */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className="text-sm font-semibold text-ink-gray-900 dark:text-ink-white mb-1">App Credentials</h3>
                  <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-4">
                    Stored encrypted — full secret is never shown.
                  </p>

                  {isLoadingCreds ? (
                    <InlineSpinner />
                  ) : credentialLoadError ? (
                    <div className="space-y-3">
                      <AlertBanner>{credentialLoadError}</AlertBanner>
                      <CompactButton
                        type="button"
                        onClick={() => setCredentialReloadKey((key) => key + 1)}
                      >
                        Retry
                      </CompactButton>
                    </div>
                  ) : credentialInfo?.has_credentials ? (
                    <>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                        <div>
                          <label className={FIELD_LABEL_CLASS}>Client ID</label>
                          <Input
                            type="text"
                            value={credentialInfo.client_id}
                            disabled
                            className="font-mono"
                          />
                        </div>
                        <div>
                          <label className={FIELD_LABEL_CLASS}>Client Secret</label>
                          <Input
                            type="text"
                            value={`••••••••${credentialInfo.last4_secret}`}
                            disabled
                            className="font-mono"
                          />
                        </div>
                      </div>

                      {credSuccess && (
                        <p className="text-ink-green-600 dark:text-ink-green-400 text-sm mb-3">Credentials updated successfully.</p>
                      )}

                      {!isRotating && (
                        <CompactButton
                          type="button"
                          onClick={() => { setIsRotating(true); setCredError(null); }}
                        >
                          <RefreshCw className="w-4 h-4" />
                          Rotate Credentials
                        </CompactButton>
                      )}

                      {isRotating && (
                        <div className="mt-4 p-4 border border-edge-gray-200 dark:border-edge-gray-600 rounded-lg bg-surface-muted-deep dark:bg-surface-muted-deep/30 space-y-4">
                          <p className="text-sm font-medium text-ink-gray-700 dark:text-ink-gray-300">Enter new credentials</p>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Input
                              type="text"
                              value={rotateClientId}
                              onChange={(e) => setRotateClientId(e.target.value)}
                              placeholder="New Client ID"
                              autoComplete="off"
                            />
                            <Input
                              type="password"
                              value={rotateClientSecret}
                              onChange={(e) => setRotateClientSecret(e.target.value)}
                              placeholder="New Client Secret"
                              autoComplete="new-password"
                            />
                          </div>
                          {credError && <p className="text-ink-red-500 text-xs">{credError}</p>}
                          <div className="flex gap-3">
                            <CompactPrimaryButton
                              type="button"
                              onClick={handleSaveCredentials}
                              disabled={isSavingCreds}
                            >
                              {isSavingCreds ? "Saving..." : "Save"}
                            </CompactPrimaryButton>
                            <CompactButton
                              type="button"
                              onClick={() => { setIsRotating(false); setRotateClientId(""); setRotateClientSecret(""); setCredError(null); }}
                            >
                              Cancel
                            </CompactButton>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-ink-hex-d97706 dark:text-ink-amber-400 mb-4">No credentials stored yet. Enter them below to enable OAuth for this provider.</p>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Input
                          type="text"
                          value={rotateClientId}
                          onChange={(e) => setRotateClientId(e.target.value)}
                          placeholder="Client ID"
                          autoComplete="off"
                        />
                        <Input
                          type="password"
                          value={rotateClientSecret}
                          onChange={(e) => setRotateClientSecret(e.target.value)}
                          placeholder="Client Secret"
                          autoComplete="new-password"
                        />
                      </div>
                      {credError && <p className="text-ink-red-500 text-xs mt-2">{credError}</p>}
                      <CompactPrimaryButton
                        type="button"
                        onClick={handleSaveCredentials}
                        disabled={isSavingCreds}
                        className="mt-4"
                      >
                        {isSavingCreds ? "Saving..." : "Save Credentials"}
                      </CompactPrimaryButton>
                    </>
                  )}
                </div>

                {/* Section 4: Scopes */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>Configuration Details</h3>
                  <div>
                    <label className={FIELD_LABEL_CLASS}>
                      Scopes <span className="text-ink-red-500">*</span>
                      <FormInfo content="JSON configuration defining the OAuth permissions this provider requests." />
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        name="scopes_path"
                        value={values.scopes_path}
                        disabled
                        error={Boolean(errors.scopes_path && submitCount > 0)}
                        className="flex-1"
                      />
                      {values.scopes_path && !providerDraft.isEditingScopes && (
                        <CompactButton
                          type="button"
                          onClick={providerDraft.openScopesEditor}
                          disabled={Boolean(scopesLoadError)}
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </CompactButton>
                      )}
                    </div>
                    <p className={HELP_TEXT_CLASS}>JSON file with OAuth scopes</p>
                    {scopesLoadError && (
                      <p className="text-xs text-ink-red-500 mt-1">{scopesLoadError}</p>
                    )}
                    {errors.scopes_path && submitCount > 0 && (
                      <p className="text-ink-red-500 text-xs mt-1">{errors.scopes_path}</p>
                    )}
                  </div>

                  {/* Scopes JSON Editor (shown when editing) */}
                  {providerDraft.isEditingScopes && (
                    <FileEditorSection
                      fileType="scopes"
                      fileContent={providerDraft.scopesBuffer}
                      originalFileContent={providerDraft.scopesBufferBaseline}
                      onChange={providerDraft.updateScopesBuffer}
                      onSave={providerDraft.applyScopes}
                      onCancel={providerDraft.discardScopes}
                      saveLabel="Apply"
                      cancelLabel="Discard"
                      jsonError={providerDraft.scopesError}
                      fileChangeDetection={{}}
                    />
                  )}
                </div>

                {/* Section 4: Status */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                      <Toggle checked={values.enabled} onChange={(checked) => setFieldValue("enabled", checked)} />
                      <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                        Published
                      </label>
                    </div>
                    <p className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400">
                      Unpublishing blocks new connections and takes every agent on this provider out of service.
                    </p>
                  </div>
                </div>

                <EntityAuditStrip auditInfo={providerAudit} />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProviderPage;
